import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserFirebaseService } from './user-firebase.service';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class GtmService {

  constructor(
    private router: Router,
    private userFirebaseService: UserFirebaseService,
  ) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.registrarPageview(event.urlAfterRedirects);
      });
  }

  private registrarPageview(urlAfterRedirects: string): void {
    if (urlAfterRedirects.startsWith('/admin')) {
      return;
    }
    if (this.userFirebaseService.esStaff) {
      return;
    }

    // page_path/page_title quedan sin resetear a propósito: son contexto de
    // "página actual" válido para eventos de click que ocurran después, hasta
    // la próxima navegación.
    this.push('virtual_page_view', {
      page_path: urlAfterRedirects,
      page_title: document.title,
    }, false);
  }

  /**
   * Empuja un evento explícito al dataLayer. Preferido por sobre triggers de GTM
   * basados en selector CSS: con componentes de Angular Material el click puede
   * caer en un elemento interno (ripple, span de label) que no tiene el
   * data-gtm-id, haciendo que el trigger de click nunca matchee.
   *
   * Por default, limpia (resetea a undefined) las keys de `extra` inmediatamente
   * después de emitirlas: el dataLayer de GTM acumula/mergea el estado entre
   * pushes, así que sin este reset un valor como `cta_id` o `salida_id` de un
   * click queda "pegado" y aparece también en eventos posteriores que no
   * tienen nada que ver (otro click, otro virtual_page_view). Pasar
   * `resetAfter: false` solo para datos que sí querés que persistan como
   * contexto (por ejemplo page_path/page_title en el pageview).
   */
  public push(eventName: string, extra: Record<string, any> = {}, resetAfter = true): void {
    if (this.userFirebaseService.esStaff) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...extra });

    if (resetAfter) {
      const keys = Object.keys(extra);
      if (keys.length) {
        const reset: Record<string, undefined> = {};
        keys.forEach((k) => (reset[k] = undefined));
        window.dataLayer.push(reset);
      }
    }
  }
}
